import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/shared";
import { ArticleView } from "@/components/ArticleView";
import { categoryLabel } from "@/lib/content";
import NotFound from "@/pages/NotFound";

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(undefined);

  useEffect(() => {
    setArticle(undefined);
    api
      .get(`/articles/${slug}`)
      .then(({ data }) => setArticle(data))
      .catch(() => setArticle(null));
  }, [slug]);

  if (article === undefined) {
    return <div className="flex min-h-[60vh] items-center justify-center text-bz-slate" data-testid="article-loading">Chargement…</div>;
  }
  if (article === null) return <NotFound />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.summary,
    articleSection: categoryLabel(article.category),
    datePublished: article.published_at,
    dateModified: article.updated_at,
    author: { "@type": "Organization", name: "BENZENATI" },
    publisher: { "@type": "Organization", name: "BENZENATI" },
  };

  return (
    <>
      <Seo
        title={`${article.title} — BENZENATI`}
        description={article.summary}
        path={`/veille/${article.slug}`}
        image={article.image}
        type="article"
        jsonLd={jsonLd}
      />

      <article className="border-b border-bz-line" data-testid="article-page">
        <div className="mx-auto max-w-3xl px-6 py-16 lg:py-24">
          <Reveal>
            <Link to="/veille" data-testid="article-back-link" className="inline-flex items-center gap-2 text-sm text-bz-slate transition-colors hover:text-bz-navy">
              <ArrowLeft size={16} strokeWidth={1.5} /> Retour aux décryptages
            </Link>
            <div className="mt-8">
              <ArticleView article={article} />
            </div>
          </Reveal>
        </div>
      </article>

      {article.related?.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-24" data-testid="article-related">
          <span className="bz-label text-bz-petrol">À lire également</span>
          <div className="mt-10 grid grid-cols-1 gap-10 md:grid-cols-2">
            {article.related.map((a) => (
              <Link
                key={a.slug}
                to={`/veille/${a.slug}`}
                data-testid={`related-${a.slug}`}
                className="group flex flex-col border border-bz-line bg-white p-8 transition-colors duration-300 hover:border-bz-petrol"
              >
                <span className="bz-label text-bz-petrol">{categoryLabel(a.category)}</span>
                <h3 className="mt-4 font-serif text-2xl leading-snug text-bz-navy">{a.title}</h3>
                <p className="mt-4 text-base leading-relaxed text-bz-slate">{a.summary}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
